<template>
   <div>
      <navbar-component />
      <main>
         <!-- Hero: matches FracSupport / WaveData -->
         <section
            class="relative pt-16 pb-32 flex content-center items-center justify-center min-header"
         >
            <div
               class="absolute top-0 w-full h-full bg-center bg-cover"
               :style="{ backgroundImage: `url(${headerImage})` }"
            >
               <span
                  id="blackOverlay"
                  class="w-full h-full absolute opacity-75 bg-black"
               ></span>
            </div>
            <div class="container relative mx-auto px-4">
               <div class="items-center flex flex-wrap">
                  <div
                     class="w-full lg:w-10/12 px-4 ml-auto mr-auto text-center"
                  >
                     <span
                        class="inline-block bg-blue-600 text-white text-xs font-bold tracking-wider uppercase px-3 py-1 rounded-full mb-5 shadow-sm"
                     >
                        Legacy DVCommand suite
                     </span>
                     <h1 class="text-white font-semibold text-4xl md:text-5xl mb-4">
                        DVCommand Suite of Products
                     </h1>
                     <p class="text-lg md:text-xl text-blueGray-200 max-w-3xl mx-auto leading-relaxed">
                        Proven frac control software for the data van—backed by
                        25+ years of experience in pump control, blender control,
                        hydration, and chemical units with real-time acquisition and reporting.
                        Still supported in the field for existing fleets.
                     </p>
                  </div>
               </div>
            </div>
         </section>

         <section class="relative pb-20 md:pb-24 bg-blueGray-200 -mt-24">
            <div class="container mx-auto px-4 md:px-6">
               <!-- Centered intro below hero (WaveEdge / PumpLink7 pattern; pt clears -mt-24 overlap) -->
               <div
                  class="text-center max-w-3xl mx-auto pt-14 md:pt-20 mb-8 md:mb-10 px-4"
               >
                  <h2
                     class="text-3xl md:text-4xl font-bold text-blueGray-800 mb-3 leading-tight"
                  >
                     Integrated field control
                  </h2>
                  <div class="flex justify-center mb-5">
                     <span
                        class="inline-block w-24 h-1 rounded bg-blue-500"
                        aria-hidden="true"
                     ></span>
                  </div>
                  <p class="text-lg text-blueGray-600 leading-relaxed">
                     Each product works with DVCommand in the data van or at the
                     asset—consistent operator workflows, real-time frac data acquisition,
                     pump and blender control, and robust safety logic.
                  </p>
               </div>

               <!-- Next-gen callout: WaveCommand7 replaces DVCommand in many apps -->
               <div
                  class="max-w-4xl mx-auto mb-10 md:mb-12 rounded-xl border border-blueGray-700 bg-blueGray-900 shadow-lg p-6 md:p-8"
               >
                  <p
                     class="text-xs font-bold uppercase tracking-wider text-lightBlue-300 mb-2"
                  >
                     Looking for the next generation?
                  </p>
                  <h3 class="text-2xl font-bold text-white mb-3">
                     WaveCommand7 is the ground-up rebuild
                  </h3>
                  <p class="text-blueGray-300 mb-4 leading-relaxed">
                     New architecture on WaveEdge and WaveData Cloud, new hardware
                     path, fuller treatment plans, wellbore diagrams, richer alarms,
                     and more—while remaining compatible with existing AMI equipment.
                     For many applications, WaveCommand7 is the replacement for
                     legacy DVCommand.
                  </p>
                  <router-link
                     to="/wavecommand7"
                     class="inline-block bg-blue-600 hover:bg-blue-500 text-white font-bold py-3 px-6 rounded-lg transition-colors duration-200"
                  >
                     Explore WaveCommand7 <i class="fas fa-arrow-right ml-2"></i>
                  </router-link>
               </div>

               <div
                  class="dv-suite-cards-wrap mx-auto flex flex-col gap-8 md:gap-10"
               >
                  <article
                     v-for="(product, productIndex) in suiteProducts"
                     :key="product.title"
                     class="rounded-xl shadow-lg overflow-hidden border suite-card"
                     :class="
                        product.theme === 'dark'
                           ? 'bg-blueGray-900 border-blueGray-700'
                           : 'bg-white border-blueGray-100'
                     "
                  >
                     <!-- Even index: image left; odd: image right (lg:order-* — lg:flex-row-reverse is often purged from tailwind.css) -->
                     <div class="flex flex-col lg:flex-row lg:items-stretch">
                        <div
                           class="w-full lg:w-6/12 flex-shrink-0 flex items-center justify-center py-9 px-6 md:px-10 suite-image-panel border-b"
                           :class="[
                              product.theme === 'dark'
                                 ? 'bg-blueGray-800 border-blueGray-700'
                                 : 'from-blueGray-50 to-blueGray-100 border-blueGray-200 bg-gradient-to-b',
                              productIndex % 2 === 0 ? 'lg:order-1' : 'lg:order-2',
                              productIndex % 2 === 0
                                 ? 'suite-image-panel--img-left'
                                 : 'suite-image-panel--img-right',
                           ]"
                        >
                           <!-- Fixed frame so wide assets scale by width; tall assets by height (same visual weight) -->
                           <div class="suite-image-frame">
                              <img
                                 :src="product.image"
                                 :alt="product.alt"
                                 class="rounded-lg shadow-md border p-2 md:p-3 suite-product-img"
                                 :class="
                                    product.theme === 'dark'
                                       ? 'bg-blueGray-900 border-blueGray-600'
                                       : 'bg-white border-blueGray-100'
                                 "
                              />
                           </div>
                        </div>
                        <div
                           class="w-full lg:w-6/12 min-w-0 p-6 md:p-8 lg:px-10 lg:py-8 product-body"
                           :class="productIndex % 2 === 0 ? 'lg:order-2' : 'lg:order-1'"
                        >
                           <div
                              class="text-center lg:text-left max-w-3xl mx-auto lg:mx-0 mb-5"
                           >
                              <h3
                                 class="text-2xl md:text-3xl font-semibold leading-tight flex flex-wrap items-center justify-center lg:justify-start gap-2"
                                 :class="
                                    product.theme === 'dark'
                                       ? product.titleClassDark
                                       : product.titleClass
                                 "
                              >
                                 <i
                                    :class="[
                                       'fas',
                                       product.icon,
                                       product.theme === 'dark'
                                          ? product.iconColorClassDark
                                          : product.iconColorClass,
                                       'text-xl md:text-2xl',
                                    ]"
                                 ></i>
                                 {{ product.title }}
                              </h3>
                              <p
                                 v-if="product.subtitle"
                                 class="mt-1.5 text-sm font-medium"
                                 :class="
                                    product.theme === 'dark'
                                       ? 'text-blueGray-400'
                                       : 'text-blueGray-500'
                                 "
                              >
                                 {{ product.subtitle }}
                              </p>
                              <div
                                 class="mt-4 text-base leading-relaxed"
                                 :class="
                                    product.theme === 'dark'
                                       ? 'text-blueGray-300'
                                       : 'text-blueGray-600 md:text-lg'
                                 "
                                 v-html="product.blurbHtml"
                              ></div>
                              <p
                                 v-if="product.title === 'PumpLink'"
                                 class="mt-3 text-base leading-relaxed"
                                 :class="
                                    product.theme === 'dark'
                                       ? 'text-blueGray-300'
                                       : 'text-blueGray-600'
                                 "
                              >
                                 <span
                                    class="font-semibold"
                                    :class="
                                       product.theme === 'dark'
                                          ? 'text-white'
                                          : 'text-blueGray-800'
                                    "
                                    >New:</span
                                 >
                                 We now have
                                 <router-link
                                    to="/pumplink7"
                                    class="font-medium underline transition-opacity hover:opacity-80"
                                    :class="product.linkClass"
                                    >PumpLink7</router-link
                                 >.
                              </p>
                              <p
                                 v-if="product.title === 'BlenderLink'"
                                 class="mt-3 text-base leading-relaxed"
                                 :class="
                                    product.theme === 'dark'
                                       ? 'text-blueGray-300'
                                       : 'text-blueGray-600'
                                 "
                              >
                                 <span
                                    class="font-semibold"
                                    :class="
                                       product.theme === 'dark'
                                          ? 'text-white'
                                          : 'text-blueGray-800'
                                    "
                                    >New:</span
                                 >
                                 We now have
                                 <router-link
                                    to="/blenderlink7"
                                    class="font-medium underline transition-opacity hover:opacity-80"
                                    :class="product.linkClass"
                                    >BlenderLink7</router-link
                                 >.
                              </p>
                              <p
                                 v-if="product.title === 'DVCommand'"
                                 class="mt-3 text-base leading-relaxed"
                                 :class="
                                    product.theme === 'dark'
                                       ? 'text-blueGray-300'
                                       : 'text-blueGray-600'
                                 "
                              >
                                 <span
                                    class="font-semibold"
                                    :class="
                                       product.theme === 'dark'
                                          ? 'text-white'
                                          : 'text-blueGray-800'
                                    "
                                    >New:</span
                                 >
                                 Explore
                                 <router-link
                                    to="/wavecommand7"
                                    class="font-medium underline transition-opacity hover:opacity-80 text-blue-600"
                                    >WaveCommand7</router-link
                                 >
                                 for the ground-up rebuild, and the redesigned
                                 <router-link
                                    to="/mpc"
                                    class="font-medium underline transition-opacity hover:opacity-80 text-blue-600"
                                    >Datavan MPC</router-link
                                 >
                                 for multipump control.
                              </p>
                           </div>

                           <div
                              class="rounded-lg px-4 py-3 md:px-5 md:py-4 border suite-features"
                              :class="
                                 product.theme === 'dark'
                                    ? 'bg-blueGray-800/80 border-blueGray-600'
                                    : 'bg-blueGray-50 border-blueGray-100'
                              "
                           >
                              <h4
                                 class="text-sm font-bold uppercase tracking-wider mb-3 flex items-center gap-2"
                                 :class="
                                    product.theme === 'dark'
                                       ? 'text-blueGray-400'
                                       : 'text-blueGray-500'
                                 "
                              >
                                 <span
                                    class="inline-block w-6 h-0.5 rounded-full shrink-0"
                                    :class="product.accentBarClass"
                                 ></span>
                                 Capabilities &amp; features
                              </h4>
                              <ul class="space-y-2 text-base md:text-[1.0625rem] leading-normal">
                                 <li
                                    v-for="(feat, fi) in product.features"
                                    :key="fi"
                                    class="flex items-start gap-4"
                                 >
                                    <i
                                       class="fas fa-check-circle mt-1 shrink-0 text-sm md:text-base"
                                       :class="product.checkClass"
                                    ></i>
                                    <span
                                       :class="
                                          product.theme === 'dark'
                                             ? 'text-blueGray-300'
                                             : 'text-blueGray-700'
                                       "
                                    >
                                       <span
                                          class="font-semibold"
                                          :class="product.featureLabelClass"
                                          >{{ feat.label }}:</span
                                       >
                                       {{ feat.text }}
                                    </span>
                                 </li>
                              </ul>
                           </div>
                        </div>
                     </div>
                  </article>
               </div>
            </div>
         </section>
      </main>
      <footer-component />
   </div>
</template>
<script>
import NavbarComponent from "@/components/Navbars/IndexNavbar.vue";
import FooterComponent from "@/components/Footers/Footer.vue";
import { setPageSeo, productJsonLd, breadcrumbJsonLd } from "@/utils/seo";
import headerImage from "@/assets/img/dataPipes.jpg";
import imgDVCommand from "@/assets/img/mpc/mpc-showcase.png";
import imgBlender from "@/assets/img/DVC-blender remote.jpg";
import imgPump from "@/assets/img/servicesupport.png";
import imgHydration from "@/assets/img/ami_wave_pic.jpg";
import imgChem from "@/assets/img/industrial-engineer-programming-machine-control.jpg";

export default {
   components: {
      NavbarComponent,
      FooterComponent,
   },
   mounted() {
      setPageSeo({
         title: "DVCommand Suite | Frac Control Software | AMI",
         description:
            "Proven frac control for the data van—multipump pump control, blender, hydration, and chemical units with real-time acquisition and reporting.",
         keywords:
            "DVCommand, frac control, pump control, data van software, blender control, hydraulic fracturing control, multipump control, AMI",
         path: "/dvcommand",
         jsonLd: [
            productJsonLd({
               name: "DVCommand Suite",
               description:
                  "Frac control software for the data van including multipump, blender, hydration, and chemical control.",
               path: "/dvcommand",
            }),
            breadcrumbJsonLd([
               { name: "Home", path: "/" },
               { name: "DVCommand Suite", path: "/dvcommand" },
            ]),
         ],
      });
   },
   data() {
      return {
         headerImage,
         suiteProducts: [
            {
               title: "DVCommand",
               subtitle: "MPC & Treater versions",
               alt: "DVCommand multipump pump control interface in the data van",
               image: imgDVCommand,
               theme: "light",
               titleClass: "text-blue-800",
               titleClassDark: "",
               icon: "fa-truck",
               iconColorClass: "text-blue-500",
               iconColorClassDark: "",
               accentBarClass: "bg-blue-500",
               checkClass: "text-blue-500",
               featureLabelClass: "text-blue-800",
               linkClass: "",
               blurbHtml:
                  "Industry-standard frac data acquisition, control, and reporting. The legacy suite works with our original BlenderLink, PumpLink, HydrationLink, and ChemLink units—and those systems are also supported in <a href=\"/wavecommand7\">WaveCommand7</a> for data viewing and monitoring. Works with our <a href=\"/wavedata\">WaveData Cloud</a> and third-party gear.",
               features: [
                  {
                     label: "Overview",
                     text: "Data-van control and acquisition—MPC (multi-pump) and Treater builds.",
                  },
                  {
                     label: "Centralized control",
                     text: "Remote control of pumpers, blenders, hydration, and more on the network.",
                  },
                  {
                     label: "Real-time GUI",
                     text: "Live mimic of connected equipment layout and state.",
                  },
                  {
                     label: "Graphing",
                     text: "Up to 30 channels, custom layouts and Y-axes.",
                  },
                  {
                     label: "Redundancy",
                     text: "Multiple I/O sources for one channel when you need backup inputs.",
                  },
                  {
                     label: "Export & reports",
                     text: "Data export and maintenance reporting utilities.",
                  },
                  {
                     label: "Licensing",
                     text: "View-only mode without a license, or full control with licensing.",
                  },
               ],
            },
            {
               title: "BlenderLink",
               subtitle: "",
               alt: "BlenderLink remote blender control and monitoring screen",
               image: imgBlender,
               theme: "dark",
               titleClass: "text-emerald-700",
               titleClassDark: "text-emerald-400",
               icon: "fa-blender",
               iconColorClass: "text-emerald-500",
               iconColorClassDark: "text-emerald-400",
               accentBarClass: "bg-emerald-400",
               checkClass: "text-emerald-400",
               featureLabelClass: "text-emerald-300",
               linkClass: "text-sky-400",
               blurbHtml:
                  "Real-time blender control and monitoring—chemical and proppant delivery tuned for frac performance.",
               features: [
                  {
                     label: "Scope",
                     text: "Blender interface and control logic.",
                  },
                  {
                     label: "Bumpless transfer",
                     text: "MANUAL→AUTO without process bumps.",
                  },
                  {
                     label: "Liquid chemicals",
                     text: "Several additive pumps, each with MANUAL/AUTO.",
                  },
                  {
                     label: "Remote sync",
                     text: "DVCommand remote actions mirror the local blender console.",
                  },
                  {
                     label: "Flow output",
                     text: "Suction rate on a frequency output for external gear.",
                  },
                  {
                     label: "Hydraulic engine",
                     text: "Start and power-up from software.",
                  },
               ],
            },
            {
               title: "PumpLink",
               subtitle: "",
               alt: "PumpLink frac pump control unit and diagnostics",
               image: imgPump,
               theme: "light",
               titleClass: "text-purple-700",
               titleClassDark: "",
               icon: "fa-tachometer-alt",
               iconColorClass: "text-purple-500",
               iconColorClassDark: "",
               accentBarClass: "bg-purple-500",
               checkClass: "text-purple-500",
               featureLabelClass: "text-purple-800",
               linkClass: "text-blue-600",
               blurbHtml:
                  "Pump control and diagnostics—remote monitoring, safety automation, and maintenance visibility.",
               features: [
                  {
                     label: "Scope",
                     text: "Unit-level control to engine and transmission modules.",
                  },
                  {
                     label: "ECM",
                     text: "RPM, gear, and DTCs from the engine ECM.",
                  },
                  {
                     label: "Kick-out",
                     text: "Px setpoint trips pump to neutral idle if discharge is too high.",
                  },
                  {
                     label: "Remote / local",
                     text: "DVCommand from the van or HMI on the truck.",
                  },
                  {
                     label: "Fire suppression",
                     text: "Monitoring and logging for suppression systems.",
                  },
                  {
                     label: "PEMS",
                     text: "Optional remote diagnostics—cavitation, leaks, and more.",
                  },
               ],
            },
            {
               title: "HydrationLink",
               subtitle: "",
               alt: "HydrationLink hydration unit automation and gel mixing",
               image: imgHydration,
               theme: "dark",
               titleClass: "text-pink-600",
               titleClassDark: "text-pink-400",
               icon: "fa-flask",
               iconColorClass: "text-pink-400",
               iconColorClassDark: "text-pink-300",
               accentBarClass: "bg-pink-400",
               checkClass: "text-pink-400",
               featureLabelClass: "text-pink-300",
               linkClass: "text-sky-400",
               blurbHtml:
                  "Hydration unit automation—gel quality, tank strategy, and field data in one workflow.",
               features: [
                  {
                     label: "Scope",
                     text: "Fluids and chemical mixing on the hydration unit.",
                  },
                  {
                     label: "Tank levels",
                     text: "Suction pump MANUAL when you need to manage levels.",
                  },
                  {
                     label: "Bumpless transfer",
                     text: "MANUAL→AUTO like BlenderLink.",
                  },
                  {
                     label: "Stage volume",
                     text: "Target-based time and volume estimates.",
                  },
                  {
                     label: "Flow measurement",
                     text: "Local rates with meter calibration.",
                  },
                  {
                     label: "Mixer / hydraulics",
                     text: "Panels for engines and mixers on the unit.",
                  },
               ],
            },
            {
               title: "ChemLink",
               subtitle: "",
               alt: "ChemLink chemical additive unit control panel",
               image: imgChem,
               theme: "light",
               titleClass: "text-yellow-700",
               titleClassDark: "",
               icon: "fa-vials",
               iconColorClass: "text-yellow-500",
               iconColorClassDark: "",
               accentBarClass: "bg-amber-500",
               checkClass: "text-amber-500",
               featureLabelClass: "text-amber-800",
               linkClass: "",
               blurbHtml:
                  "Chemical management and tracking—dosing automation tied into DVCommand.",
               features: [
                  {
                     label: "Scope",
                     text: "Additive measurement and control.",
                  },
                  {
                     label: "Multi-pump",
                     text: "Typically 5+ independent pumps (config dependent).",
                  },
                  {
                     label: "Concentration",
                     text: "Setpoints from gal/1000 gal and clean flow.",
                  },
                  {
                     label: "Serial out",
                     text: "Live rates, concentrations, stage totals on RS232/485.",
                  },
                  {
                     label: "Modes",
                     text: "Per-pump MANUAL and AUTO.",
                  },
               ],
            },
         ],
      };
   },
};
</script>

<style>
.min-header {
   min-height: 45vh;
}

/* Wider than max-w-5xl (not in prebuilt tailwind.css) */
.dv-suite-cards-wrap {
   max-width: 80rem;
}

/* Shared frame: wide images use full column width; tall images fill height */
.suite-image-frame {
   width: 100%;
   height: 12rem;
   display: flex;
   align-items: center;
   justify-content: center;
   box-sizing: border-box;
}
@media (min-width: 640px) {
   .suite-image-frame {
      height: 15rem;
   }
}
@media (min-width: 1024px) {
   .suite-image-frame {
      height: 20rem;
   }
}
.suite-image-frame .suite-product-img {
   box-sizing: border-box;
   max-width: 100%;
   max-height: 100%;
   width: auto;
   height: auto;
   object-fit: contain;
   display: block;
}

/* lg column divider + drop mobile bottom border (responsive border-* is often missing from prebuilt tailwind.css) */
@media (min-width: 1024px) {
   .suite-image-panel--img-left {
      border-bottom-width: 0;
      border-right-width: 1px;
      border-right-style: solid;
   }
   .suite-image-panel--img-right {
      border-bottom-width: 0;
      border-left-width: 1px;
      border-left-style: solid;
   }
}

/* Theme-aware links inside v-html blurbs */
.suite-card.bg-white .product-body a {
   color: #2563eb;
   font-weight: 500;
   text-decoration: underline;
}
.suite-card.bg-white .product-body a:hover {
   color: #1d4ed8;
}

.suite-card.bg-blueGray-900 .product-body a {
   color: #38bdf8;
   font-weight: 500;
   text-decoration: underline;
}
.suite-card.bg-blueGray-900 .product-body a:hover {
   color: #7dd3fc;
}
</style>
